import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FaCloudUploadAlt, FaLockOpen, FaServer, FaLightbulb,
  FaProjectDiagram, FaPencilRuler, FaChartLine, FaTools,
  FaCloud, FaShieldAlt, FaRobot, FaCheckCircle, FaBuilding
} from 'react-icons/fa';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import { getBrand } from '../utils/brandStyles';

/* ════════════════════════════════════════════════════════════
   MWO HOME  —  Dark editorial design
   Fonts: Syne (display) + Lora (body)
   Colors: #0D0F14 near-black · #F4F1EB cream · #00BCD4 cyan
════════════════════════════════════════════════════════════ */
const MWOHome = ({ content }) => {
  const b = getBrand('mwo');
  const serviceLinks = [
    '/services/consulting',
    '/services/managed-it',
    '/services/cloud',
    '/services/security',
    '/services/ai-consulting',
    '/services/small-business'
  ];

  const deliverItems = [
    { num: '01', title: 'Future-Proof Infrastructure',   body: 'Cloud-native and hybrid architectures that scale with your growth and ambition.' },
    { num: '02', title: 'Secure Access, Empowered Teams', body: 'Identity and access management for seamless, secure collaboration.' },
    { num: '03', title: 'Operational Excellence',        body: 'Automated, monitored environments engineered for peak performance.' },
    { num: '04', title: 'AI & Intelligent Automation',   body: 'Next-generation automation that eliminates toil and surfaces real insights.' },
    { num: '05', title: 'End-to-End Cybersecurity',      body: 'Layered protection for your data, people, and critical infrastructure.' },
    { num: '06', title: 'Strategic IT Consulting',       body: 'Bespoke technology roadmaps built around your business goals.' }
  ];

  return (
    <div style={{ fontFamily: "'Barlow', sans-serif", color: '#E8E4DC', background: '#0D0F14' }}>

      {/* ── HERO ──────────────────────────────────────────── */}
      <section style={{
        minHeight: '92vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: '120px clamp(24px, 7vw, 120px) 100px',
        background: '#0D0F14',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Dot-grid atmosphere */}
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          backgroundImage: 'radial-gradient(rgba(0,188,212,0.06) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
          WebkitMaskImage: 'radial-gradient(ellipse 90% 90% at 50% 50%, black 40%, transparent 100%)',
          maskImage: 'radial-gradient(ellipse 90% 90% at 50% 50%, black 40%, transparent 100%)'
        }} />
        {/* Subtle glow */}
        <div style={{
          position: 'absolute', top: '10%', right: '-5%',
          width: '480px', height: '480px', pointerEvents: 'none',
          background: 'radial-gradient(circle, rgba(0,188,212,0.05) 0%, transparent 70%)'
        }} />

        {/* Cyan rule — animates in */}
        <div className="anim-slide-r" style={{
          width: '56px', height: '3px', background: '#00BCD4', marginBottom: '52px'
        }} />

        <h1
          className="anim-fade-up"
          style={{
            fontFamily: "'Barlow Condensed', sans-serif",
            fontWeight: 800,
            fontSize: 'clamp(2.8rem, 8vw, 7.5rem)',
            lineHeight: 1.0,
            letterSpacing: '-0.03em',
            color: '#F4F1EB',
            maxWidth: '960px',
            marginBottom: '36px'
          }}
        >
          {content.heroTitle}
        </h1>

        <p
          className="anim-fade-up delay-1"
          style={{
            fontFamily: "'Barlow', sans-serif",
            
            fontSize: 'clamp(1rem, 2vw, 1.2rem)',
            lineHeight: 1.8,
            color: 'rgba(244,241,235,0.78)',
            maxWidth: '520px',
            marginBottom: '56px'
          }}
        >
          {content.heroSubtitle}
        </p>

        <div className="anim-fade-up delay-2" style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
          {content.ctaUrl.startsWith('/') ? (
            <Link to={content.ctaUrl} className="btn-primary-mwo">{content.ctaText} →</Link>
          ) : (
            <a href={content.ctaUrl} target="_blank" rel="noopener noreferrer" className="btn-primary-mwo">
              {content.ctaText} →
            </a>
          )}
          <Link to="/about" style={{
            fontFamily: "'Barlow Condensed', sans-serif", fontSize: '12px', fontWeight: 700,
            letterSpacing: '0.1em', textTransform: 'uppercase',
            color: 'rgba(244,241,235,0.7)', textDecoration: 'none',
            transition: 'color 0.2s'
          }}
            onMouseEnter={e => e.currentTarget.style.color = 'rgba(244,241,235,0.9)'}
            onMouseLeave={e => e.currentTarget.style.color = 'rgba(244,241,235,0.7)'}
          >
            Learn about us →
          </Link>
        </div>
      </section>

      {/* ── WHAT WE DELIVER ───────────────────────────────── */}
      <section style={{ padding: '100px clamp(24px, 7vw, 120px)', background: '#111318' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>

          <div style={{ marginBottom: '64px' }}>
            <p style={{
              fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px',
              letterSpacing: '0.2em', textTransform: 'uppercase',
              color: '#00BCD4', marginBottom: '16px'
            }}>What We Deliver</p>
            <h2 style={{
              fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700,
              fontSize: 'clamp(1.8rem, 4vw, 3rem)',
              color: '#F4F1EB', letterSpacing: '-0.025em', lineHeight: 1.15
            }}>
              End-to-end IT expertise<br />
              for the modern business
            </h2>
          </div>

          {/* Grid of deliver items — 6 items as 3×2 (responsive) */}
          <div className="grid-6" style={{
            border: '1px solid rgba(255,255,255,0.07)',
            overflow: 'hidden',
            gap: 0
          }}>
            {deliverItems.map((item) => (
              <div key={item.num} className="mwo-deliver-card" style={{
                padding: '40px 32px',
                borderRight: '1px solid rgba(255,255,255,0.07)',
                borderBottom: '1px solid rgba(255,255,255,0.07)'
              }}>
                <div style={{
                  fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800,
                  fontSize: '11px', letterSpacing: '0.15em',
                  color: '#00BCD4', marginBottom: '20px'
                }}>{item.num}</div>
                <h3 style={{
                  fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700,
                  fontSize: b.fz.lg, color: '#F4F1EB',
                  marginBottom: '14px', lineHeight: 1.35
                }}>{item.title}</h3>
                <p style={{
                  fontFamily: "'Barlow', sans-serif",
                  fontSize: b.fz.sm, lineHeight: 1.8,
                  color: 'rgba(244,241,235,0.74)'
                }}>{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SERVICES ──────────────────────────────────────── */}
      <section style={{ padding: '100px clamp(24px, 7vw, 120px)', background: '#F4F1EB', color: '#0D0F14' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>

          <div style={{
            display: 'flex', justifyContent: 'space-between',
            alignItems: 'flex-end', flexWrap: 'wrap', gap: '24px', marginBottom: '64px'
          }}>
            <div>
              <p style={{
                fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px',
                letterSpacing: '0.2em', textTransform: 'uppercase',
                color: '#1A237E', marginBottom: '12px'
              }}>Our Services</p>
              <h2 style={{
                fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700,
                fontSize: 'clamp(1.8rem, 4vw, 3rem)',
                color: '#0D0F14', letterSpacing: '-0.025em', lineHeight: 1.1
              }}>
                Solutions built<br />for impact
              </h2>
            </div>
            <Link to="/contact" className="btn-primary-mwo" style={{
              borderColor: '#1A237E', color: '#1A237E'
            }}
              onMouseEnter={e => { e.currentTarget.style.background = '#1A237E'; e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#1A237E'; }}
            >
              Get in touch →
            </Link>
          </div>

          {/* Services — 3-col grid, responsive */}
          <div className="grid-6">
            {content.services.map((service, idx) => (
              <Link key={idx} to={serviceLinks[idx] || '/'} style={{ textDecoration: 'none' }}>
                <div className="mwo-service-card" style={{
                  background: '#fff',
                  padding: '36px 28px',
                  borderTop: '3px solid #00BCD4',
                  height: '100%', display: 'flex', flexDirection: 'column'
                }}>
                  <h3 style={{
                    fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700,
                    fontSize: '1rem', color: '#0D0F14',
                    marginBottom: '12px', lineHeight: 1.35
                  }}>{service.title}</h3>
                  <p style={{
                    fontFamily: "'Barlow', sans-serif",
                    fontSize: b.fz.sm, lineHeight: 1.75,
                    color: '#64748b', flex: 1, marginBottom: '20px'
                  }}>{service.description}</p>
                  <span style={{
                    fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700,
                    fontSize: '11px', letterSpacing: '0.1em',
                    textTransform: 'uppercase', color: '#00BCD4'
                  }}>Learn more →</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── PARTNER LOGOS ─────────────────────────────────── */}
      {content.showPartnerLogos && (
        <section style={{ padding: '80px clamp(24px, 7vw, 120px)', background: '#0D0F14' }}>
          <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
            <p style={{
              fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px',
              letterSpacing: '0.2em', textTransform: 'uppercase',
              color: 'rgba(244,241,235,0.28)', textAlign: 'center', marginBottom: '48px'
            }}>Technology Partners</p>
            <div className="partner-logo-row">
              {['aws.png','azure.png','gcp.png','microsoft365.png','okta.png','vmware.svg','omnissa.png','atlassian.png','zscaler.png'].map((logo, idx) => (
                <img key={idx} src={`/logos/${logo}`} alt={logo.split('.')[0]} className="partner-logo-img" />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

/* ════════════════════════════════════════════════════════════
   MIND IT HOME  —  Refined professional design
   Fonts: Playfair Display (display) + DM Sans (body)
   Colors: #0F1B45 deep navy · #F4F6FB light · #00BCD4 cyan
════════════════════════════════════════════════════════════ */
const MinditHome = ({ market, content }) => {
  const b = getBrand(market);
  const luxBenefits = [
    { icon: <FaCheckCircle size={18} color="#00BCD4" />, text: 'Free IT Health Check' },
    { icon: <FaCheckCircle size={18} color="#00BCD4" />, text: 'Fast Cloud Migration' },
    { icon: <FaCheckCircle size={18} color="#00BCD4" />, text: 'AI-Powered Automation' },
    { icon: <FaCheckCircle size={18} color="#00BCD4" />, text: 'Transparent Pricing' }
  ];

  const carouselSlides = market === 'lu' ? [
    { icon: <FaRobot size={44} color="#1A237E" />,         title: 'AI That Works for You',        description: 'Automate tasks, improve customer service, and gain data-driven insights.', bg: '#E1F5FE' },
    { icon: <FaCloudUploadAlt size={44} color="#1A237E" />, title: 'Move to the Cloud Faster',     description: 'We handle migration to Microsoft 365, Google Workspace, or AWS.',         bg: '#E3F2FD' },
    { icon: <FaShieldAlt size={44} color="#1A237E" />,      title: 'Stay Secure & Compliant',      description: 'GDPR-compliant security solutions tailored for Luxembourg businesses.',    bg: '#EDE7F6' },
    { icon: <FaTools size={44} color="#1A237E" />,          title: 'Ongoing Support',              description: 'Reliable IT support that keeps your business running smoothly.',          bg: '#F3E5F5' }
  ] : [
    { icon: <FaCloudUploadAlt size={44} color="#1A237E" />, title: 'Future-Proof Infrastructure', description: 'Modernize your IT backbone with cloud-native and hybrid solutions.',       bg: '#E3F2FD' },
    { icon: <FaLockOpen size={44} color="#1A237E" />,       title: 'Empower People, Secure Access', description: 'Seamless collaboration with robust identity and access management.',    bg: '#EDE7F6' },
    { icon: <FaServer size={44} color="#1A237E" />,         title: 'Operational Excellence',       description: 'Automate and manage complex environments for peak performance.',         bg: '#E1F5FE' },
    { icon: <FaLightbulb size={44} color="#1A237E" />,      title: 'Accelerate Innovation',        description: 'Embrace AI, automation, and next-generation technologies.',              bg: '#FFF3E0' },
    { icon: <FaProjectDiagram size={44} color="#1A237E" />, title: 'Strategic Product Management', description: 'Transform ideas into market-ready products with expert guidance.',       bg: '#FCE4EC' },
    { icon: <FaPencilRuler size={44} color="#1A237E" />,    title: 'Human-Centered Design',       description: 'Delight users and reduce friction with thoughtful UX.',                  bg: '#F3E5F5' }
  ];

  const serviceIcons = [
    <FaChartLine size={28} color="#1A237E" />,
    <FaTools size={28} color="#1A237E" />,
    <FaCloud size={28} color="#1A237E" />,
    <FaShieldAlt size={28} color="#1A237E" />,
    <FaRobot size={28} color="#1A237E" />,
    <FaBuilding size={28} color="#1A237E" />
  ];
  const serviceLinks = [
    '/services/consulting', '/services/managed-it', '/services/cloud',
    '/services/security', '/services/ai-consulting', '/services/small-business'
  ];

  return (
    <div style={{ fontFamily: "'Nunito Sans', 'Helvetica Neue', sans-serif", color: '#1e2847', background: '#fff' }}>

      {/* ── HERO ──────────────────────────────────────────── */}
      <section style={{
        padding: 'clamp(80px, 12vw, 140px) clamp(24px, 8vw, 120px)',
        background: 'linear-gradient(155deg, #F4F6FB 0%, #E8EDF8 60%, #EEF2FA 100%)',
        position: 'relative', overflow: 'hidden'
      }}>
        {/* Decorative glow */}
        <div style={{
          position: 'absolute', top: '-80px', right: '-80px',
          width: '480px', height: '480px', pointerEvents: 'none',
          background: 'radial-gradient(circle, rgba(0,188,212,0.07) 0%, transparent 68%)'
        }} />
        <div style={{
          position: 'absolute', bottom: '-40px', left: '10%',
          width: '320px', height: '320px', pointerEvents: 'none',
          background: 'radial-gradient(circle, rgba(26,35,126,0.05) 0%, transparent 70%)'
        }} />

        <div style={{ maxWidth: '780px', margin: '0 auto', textAlign: 'center', position: 'relative' }}>
          {/* Eyebrow pill */}
          <div className="anim-fade-in" style={{
            display: 'inline-block',
            background: 'rgba(0,188,212,0.12)',
            color: '#0097A7',
            fontWeight: 600,
            fontSize: '13px',
            letterSpacing: '0.06em',
            padding: '6px 18px',
            borderRadius: '100px',
            marginBottom: '32px'
          }}>
            {market === 'lu' ? 'Now serving Luxembourg' : 'Trusted IT expertise since 2013'}
          </div>

          <h1
            className="anim-fade-up delay-1"
            style={{
              fontFamily: "'Libre Baskerville', Georgia, serif",
              fontWeight: 700,
              fontSize: 'clamp(2.2rem, 5.5vw, 4rem)',
              lineHeight: 1.18,
              color: '#0F1B45',
              marginBottom: '24px',
              letterSpacing: '-0.02em'
            }}
          >
            {content.heroTitle}
          </h1>

          <p
            className="anim-fade-up delay-2"
            style={{
              fontSize: 'clamp(1rem, 2vw, 1.15rem)',
              lineHeight: 1.75,
              color: '#4a5568',
              marginBottom: '40px',
              maxWidth: '560px',
              margin: '0 auto 40px'
            }}
          >
            {content.heroSubtitle}
          </p>

          {market === 'lu' && (
            <div className="anim-fade-up delay-2" style={{
              display: 'flex', justifyContent: 'center',
              gap: '20px', flexWrap: 'wrap', marginBottom: '36px'
            }}>
              {luxBenefits.map((b, idx) => (
                <div key={idx} style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  fontSize: '13px', fontWeight: 600, color: '#374151'
                }}>
                  {b.icon} {b.text}
                </div>
              ))}
            </div>
          )}

          <div className="anim-fade-up delay-3">
            {market === 'lu' ? (
              <Link to="/services/assessment" className="btn-primary-mindit">{content.ctaText}</Link>
            ) : (
              <a href={content.ctaUrl} target="_blank" rel="noopener noreferrer" className="btn-primary-mindit">
                {content.ctaText}
              </a>
            )}
          </div>
        </div>
      </section>

      {/* ── WHAT WE DELIVER — Carousel ─────────────────────── */}
      <section style={{ padding: '80px clamp(20px, 5vw, 60px)', background: '#fff' }}>
        <h2 style={{
          textAlign: 'center',
          fontFamily: "'Libre Baskerville', Georgia, serif",
          color: '#0F1B45', marginBottom: '48px',
          fontSize: 'clamp(1.6rem, 3.5vw, 2.4rem)'
        }}>
          {market === 'lu' ? 'How We Help Your Business' : 'What We Deliver'}
        </h2>

        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <Carousel
            showArrows
            autoPlay
            infiniteLoop
            interval={7000}
            showStatus={false}
            showThumbs={false}
            swipeable
          >
            {Array.from({ length: Math.ceil(carouselSlides.length / 2) }, (_, i) => i * 2).map((startIdx) => (
              <div key={startIdx} style={{
                display: 'flex', justifyContent: 'center',
                gap: '24px', flexWrap: 'wrap', padding: '0 16px 48px'
              }}>
                {carouselSlides.slice(startIdx, startIdx + 2).map((card, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.35 }}
                    style={{
                      flex: '1 1 0', minWidth: '260px', maxWidth: '480px',
                      background: card.bg,
                      borderRadius: '16px',
                      boxShadow: '0 4px 24px rgba(15,27,69,0.08)',
                      padding: '36px 28px', textAlign: 'center'
                    }}
                  >
                    <div style={{ marginBottom: '20px' }}>{card.icon}</div>
                    <h3 style={{
                      fontFamily: "'Libre Baskerville', Georgia, serif",
                      fontSize: '1.25rem', color: '#0F1B45', marginBottom: '12px'
                    }}>{card.title}</h3>
                    <p style={{ fontSize: b.fz.sm, lineHeight: 1.65, color: '#374151' }}>{card.description}</p>
                  </motion.div>
                ))}
              </div>
            ))}
          </Carousel>
        </div>
      </section>

      {/* ── SERVICES GRID ─────────────────────────────────── */}
      <section style={{ padding: '80px clamp(24px, 6vw, 80px)', background: '#F4F6FB' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{
            fontFamily: "'Libre Baskerville', Georgia, serif",
            color: '#0F1B45', marginBottom: '48px',
            fontSize: 'clamp(1.6rem, 3.5vw, 2.4rem)',
            textAlign: 'center'
          }}>
            Our Services
          </h2>
          {/* 6 services → 3×2; 4 services (lu) → 2×2 */}
          <div className={content.services.length === 4 ? 'grid-4' : 'grid-6'}>
            {content.services.map((service, idx) => (
              <Link key={idx} to={serviceLinks[idx] || '/'} style={{ textDecoration: 'none', color: 'inherit' }}>
                <motion.div
                  className="mindit-service-card"
                  whileHover={{ scale: 1.02 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                  style={{
                    background: '#fff', padding: '32px 24px',
                    borderRadius: '12px', textAlign: 'center',
                    boxShadow: '0 2px 12px rgba(15,27,69,0.07)',
                    height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center'
                  }}
                >
                  <div style={{ marginBottom: '16px' }}>{serviceIcons[idx]}</div>
                  <h3 style={{
                    fontSize: b.fz.lg, fontWeight: 600,
                    color: '#0F1B45', marginBottom: '10px', lineHeight: 1.35
                  }}>{service.title}</h3>
                  <p style={{ fontSize: b.fz.sm, lineHeight: 1.65, color: '#64748b' }}>{service.description}</p>
                </motion.div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── PARTNER LOGOS ─────────────────────────────────── */}
      {content.showPartnerLogos && (
        <section style={{ padding: '72px clamp(24px, 6vw, 80px)', background: '#fff' }}>
          <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
            <p style={{
              textAlign: 'center', fontSize: '12px',
              letterSpacing: '0.14em', textTransform: 'uppercase',
              color: '#94a3b8', marginBottom: '40px'
            }}>
              {market === 'lu' ? 'Trusted Technology Partners' : 'Our Technology Partners'}
            </p>
            <div className="partner-logo-row">
              {['aws.png','azure.png','gcp.png','microsoft365.png','okta.png','vmware.svg','omnissa.png','atlassian.png','zscaler.png'].map((logo, idx) => (
                <img key={idx} src={`/logos/${logo}`} alt={logo.split('.')[0]} className="partner-logo-img" />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

/* ─── Router ──────────────────────────────────────────────── */
const Home = ({ market, content }) => {
  if (market === 'mwo') return <MWOHome content={content} />;
  return <MinditHome market={market} content={content} />;
};

export default Home;
